import { BillingAccountBlock } from "@/admin/accounts/BillingAccountBlock.tsx";
import {
    AssignedToItem,
    type GQIpAddressItem,
    type IpAddressAssignedTo,
} from "@/admin/floating_ips/FloatingIpsTable.tsx";
import { UserLink } from "@/admin/users/UserLink.tsx";
import { Badge } from "@/components/Badge.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { ContentPane } from "@/components/ContentPane.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { AdminDate, LongDate } from "@/components/l10n/DateFormat.tsx";
import { DetailWithIcon } from "@/components/ResourceWithIcon.tsx";
import { Separator } from "@/components/Separator.tsx";
import { Spacer } from "@/components/Spacer.tsx";
import { DetailsHolder, DetailsTable } from "@/components/table/DetailsTable.tsx";
import { DetailsTableRow } from "@/components/table/DetailsTableRow.tsx";
import { WSmartTable } from "@/components/table/WTable.tsx";
import { ViewTitle } from "@/components/Title.tsx";
import { useApiClient } from "@/modules/api/apiClient.store.ts";
import { getResponseData } from "@/modules/api/apiClient.ts";
import { getResourceIconClass } from "@/modules/api/resourceTypes.tsx";
import { LocationDetail } from "@/modules/location/LocationDetail.tsx";
import { UNASSIGNED_RESOURCE } from "@/modules/network/ipAddress/resourceId.ts";
import { cn } from "@/utils/classNames.ts";
import { apiUnixToDate } from "@warrenio/api-spec/conversion";
import type { IpAddressActionLogItem } from "@warrenio/api-spec/spec.oats.gen";
import { getOwn } from "@warrenio/utils/collections/getOwnProperty";
import { useState } from "react";

export function FloatingDetailView({ item }: { item: GQIpAddressItem }) {
    return (
        <>
            <FloatingDetails item={item} />
            {!item.is_ipv6 && (
                <>
                    <Separator />
                    <FloatingEvents key={item.uuid} item={item} />
                </>
            )}
        </>
    );
}

function FloatingDetails({ item }: { item: GQIpAddressItem }) {
    const { address, billing_account_object, name, assigned_to, _location, is_ipv6 } = item;

    return (
        <ContentPane className="VStack gap-4">
            <ViewTitle title={name ? name : address} />

            <DetailsHolder>
                <DetailsTable>
                    <DetailsTableRow title="Name">{name}</DetailsTableRow>

                    <DetailsTableRow title="Type">
                        <DetailWithIcon
                            icon={
                                <MaskIcon
                                    className={cn("size-0.75rem text-primary", getResourceIconClass("ip_address"))}
                                />
                            }
                            title={is_ipv6 ? "Static IPv6" : "Floating IPv4"}
                        />
                    </DetailsTableRow>

                    <DetailsTableRow title="Public IPv4">
                        <ClipBoardTooltip>{address}</ClipBoardTooltip>
                    </DetailsTableRow>

                    <DetailsTableRow title="Status">
                        {assigned_to !== UNASSIGNED_RESOURCE ? (
                            <Badge color="success">Assigned</Badge>
                        ) : (
                            <Badge color="error">Unassigned</Badge>
                        )}
                    </DetailsTableRow>

                    <DetailsTableRow title="Assigned to">
                        <AssignedToItem item={item} />
                    </DetailsTableRow>
                </DetailsTable>

                <Spacer />

                <DetailsTable>
                    <DetailsTableRow title="Created">
                        {item.created_at ? <LongDate date={apiUnixToDate(item.created_at as number)} /> : "-"}
                    </DetailsTableRow>

                    <LocationDetail slug={_location ?? ""} />

                    <DetailsTableRow title="Billing account">
                        <BillingAccountBlock showId value={billing_account_object} />
                    </DetailsTableRow>
                </DetailsTable>
            </DetailsHolder>
        </ContentPane>
    );
}

const actionColors: Record<string, string> = {
    unassign: "color-error",
    assign: "color-success",
};

function FloatingEvents({ item }: { item: GQIpAddressItem }) {
    const { _location, address } = item;
    const api = useApiClient();
    const [events, setEvents] = useState<IpAddressActionLogItem[]>();

    async function getEvents() {
        const response = getResponseData(
            await api.GET("/{location}/network/admin/ip_addresses/{address}/log", {
                params: { path: { address: address, location: _location ?? "" } },
            }),
        );

        setEvents([...response].reverse());
    }

    const isLoaded = events !== undefined;

    return (
        <ContentPane className="VStack gap-3">
            <div className="flex justify-between items-center">
                <div className="font-size-subtitle">Action log</div>

                <WButton
                    action={() => getEvents()}
                    color="primary"
                    size="md"
                    variant="ghost"
                    icon={isLoaded ? "i-lucide:refresh-cw" : "i-lucide:arrow-down-to-line"}
                >
                    {isLoaded ? "Reload events" : "Load events"}
                </WButton>
            </div>

            {!!events && (
                <WSmartTable
                    items={events}
                    hasViewAllButton
                    head={
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Action</th>
                                <th>Address</th>
                                <th>Assigned to</th>
                                <th>User ID</th>
                                <th>Acting User ID</th>
                            </tr>
                        </thead>
                    }
                >
                    {(entry, index) => {
                        // XXX: This is missing `service_uuid`
                        const { acting_user_id, action, address, created_at, user_id, lb_instance_uuid, vm_uuid } =
                            entry;
                        const isOwner = acting_user_id != null && user_id === acting_user_id;
                        const userLabel = isOwner ? <span className="text-muted">Owner</span> : undefined;
                        const color = getOwn(actionColors, action);

                        const assignment: IpAddressAssignedTo = vm_uuid
                            ? {
                                  assigned_to: vm_uuid,
                                  assigned_to_resource_type: "virtual_machine",
                              }
                            : lb_instance_uuid
                              ? {
                                    assigned_to: lb_instance_uuid,
                                    assigned_to_resource_type: "load_balancer",
                                }
                              : {
                                    assigned_to: null,
                                    assigned_to_resource_type: null,
                                };

                        return (
                            <tr key={index}>
                                <td>
                                    <AdminDate date={created_at} />
                                </td>
                                <td>
                                    <span className={color}>{action}</span>
                                </td>
                                <td>{address}</td>
                                <td>
                                    <AssignedToItem compact item={assignment} />
                                </td>
                                <td>
                                    <UserLink value={user_id} label={user_id} />
                                </td>
                                <td>
                                    <UserLink value={acting_user_id} label={userLabel} />
                                </td>
                            </tr>
                        );
                    }}
                </WSmartTable>
            )}
        </ContentPane>
    );
}
